import { useRevalidator } from '@remix-run/react';
import { useMutation } from '@tanstack/react-query';

import { useEnsureCsrfToken } from '~/context/CsrfContext';

import { submitCommunityAction } from './useCommunityActionBase';

const SUBSCRIBE_TO_GATE_LIST_ACTION = '/community_list_subscription';

export interface GateListSubscription {
  status: string;
  subscription_status: string;
  resubscribed: boolean;
}

interface SubscribeToGateListVariables {
  spaceSlug?: string;
}

// Access is checked on every request, so reloading the layout opens the channel, or offers to join the community, once
// the member is on the list.
export const useSubscribeToGateListMutation = ({ onSubscribed }: { onSubscribed?: () => void } = {}) => {
  const ensureCsrfToken = useEnsureCsrfToken();
  const revalidator = useRevalidator();

  const mutation = useMutation({
    mutationFn: async ({ spaceSlug }: SubscribeToGateListVariables) => {
      const fields: Record<string, string> = spaceSlug ? { space_slug: spaceSlug } : {};
      const response = await submitCommunityAction(SUBSCRIBE_TO_GATE_LIST_ACTION, fields, await ensureCsrfToken());

      return response as GateListSubscription;
    },
    onSuccess: ({ status }) => {
      onSubscribed?.();
      if (status === 'active') revalidator.revalidate();
    },
  });

  const isReloading = mutation.data?.status === 'active' && revalidator.state === 'loading';

  return { ...mutation, isPending: mutation.isPending || isReloading };
};
